import { ColumnDef } from '@tanstack/react-table';
import { TagValueDisplay } from 'src/components/TagsFormBody/TagValueCell';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { CustomNumericCell } from 'src/tables/ListingTable/configs/CustomNumericCell';
import { TableShimmeringDiv } from 'src/tables/Table';
import {
  SECTION_TYPE_TO_PRECISION_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';
import { TagsValueType } from 'src/WebApiController';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

interface TagColumnOptions<TData> {
  /** Tag name (used as column ID) */
  tagName?: string;
  /** Column ID (alternative to tagName) */
  columnId?: string;
  /** Section type for precision settings */
  sectionType?: SectionType;
  /** Accessor function to get tags array from data */
  tagsAccessor: (data: TData) => any[] | null | undefined;
  /** Accessor function to get currency code from data */
  currencyAccessor: (data: TData) => string | undefined;
  /** Whether sorting is enabled for this column. Pass false in Search V2 contexts
   * where sorting is server-side and tag field names are not registered in the
   * backend sort mapper. */
  enableSorting?: boolean;
}

/**
 * Factory function to create tag column definitions for tables.
 * Handles both numeric (Int/Decimal) and non-numeric tag values.
 *
 * @example
 * createTagColumn({
 *   tagName: 'CustomTag',
 *   sectionType: SectionType.Sales,
 *   tagsAccessor: (data) => data?.sale?.tags,
 *   currencyAccessor: (data) => data?.sale?.currency,
 * })
 */
export const createTagColumn = <TData,>(
  options: TagColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    tagName,
    columnId,
    sectionType = SectionType.Sales,
    tagsAccessor,
    currencyAccessor,
    enableSorting,
  } = options;

  const id = columnId ?? tagName;
  if (!id) {
    throw new Error('Either tagName or columnId must be provided');
  }

  return {
    id,
    ...(enableSorting !== undefined && { enableSorting }),
    header: () => (
      <TableHeaderCellDiv align="right">
        <span style={{ overflowX: 'clip', textOverflow: 'ellipsis' }}>
          {id}
        </span>
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => {
      const tags = tagsAccessor(data);
      const tag = tags?.find((t) => t.key === id);
      if (
        tag?.valueType === TagsValueType.Int ||
        tag?.valueType === TagsValueType.Decimal
      ) {
        return tag ? parseFloat(tag.value) : null;
      }
      return tag?.value;
    },
    cell: function Cell({ getValue, row: { original }, table }) {
      const { value: storedColumnNumberPrecisions = {} } =
        useServerUserSetting<FormatOptionEntries>({
          id: SECTION_TYPE_TO_PRECISION_SETTING[
            table.options.meta?.sectionType ?? sectionType
          ],
        });
      const tags = tagsAccessor(original);
      const tag = tags?.find((t) => t.key === id);

      if (
        tag?.valueType === TagsValueType.Int ||
        tag?.valueType === TagsValueType.Decimal
      ) {
        const userDefinedPrecision = storedColumnNumberPrecisions[id];

        return (
          <CustomNumericCell
            columnId={id}
            userDefinedPrecision={userDefinedPrecision}
            value={getValue<number | null>()}
            isLoading={Boolean(!original || !tags)}
            shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
            sectionType={table.options.meta?.sectionType ?? sectionType}
            matchingTag={tag}
            currencyCode={currencyAccessor(original)}
          />
        );
      }

      if (!original || !tags) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!tag) {
        return null;
      }

      return (
        <TableCellDiv align="right">
          <TagValueDisplay tag={tag} displayMode />
        </TableCellDiv>
      );
    },
  };
};
